/**
 * Produktion och kostnad. Körsträcka och bränsle per månad, totalkostnad för perioden.
 * Bara designförhandsvisningen byter ut servicekostnadskortet mot det här.
 */

import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import api from '@/services/api/client';
import { ROUTES } from '@/constants/routes';
import { useAuthStore } from '@/store/authStore';
import { useLanguage, useTranslation } from '@/store/languageStore';
import {
  chartScale,
  formatAxisTick,
  formatCompactAmount,
  formatKrPerKm,
  percentChange,
  rollingYearRange,
  seriesForSpan,
  type ChartScale,
  type ProductionPoint,
  type ProductionSpan,
} from '@/utils/productionCost';

interface SummaryResponse {
  success: boolean;
  data?: {
    kpis?: { totalTco?: number; avgCostPerKm?: number; totalVehicles?: number };
    trend?: { period: string; distanceKm: number; fuelCost: number }[];
  };
}

const SPANS: ProductionSpan[] = ['months', 'year', 'quarter'];

function monthLabel(period: string, locale: string): string {
  if (period.includes('Q')) return period.slice(5);
  const [year, month] = period.split('-').map(Number);
  return new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(year, month - 1, 1)).replace('.', '');
}

function tickRatio(scale: ChartScale, tick: number): number {
  if (scale.max <= scale.min) return 1;
  return (scale.max - tick) / (scale.max - scale.min);
}

function AxisPlot({ scale, children }: { scale: ChartScale; children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-[18rem]">
      <div className="relative w-11 shrink-0 text-[10px] tabular-nums leading-none text-textTertiary">
        {scale.ticks.map((tick, index) => {
          const ratio = tickRatio(scale, tick);
          const shift = ratio <= 0 ? '0%' : ratio >= 1 ? '-100%' : '-50%';
          return (
            <div
              key={`${tick}-${index}`}
              className="absolute right-0 pr-1"
              style={{ top: `${ratio * 100}%`, transform: `translateY(${shift})` }}
            >
              {formatAxisTick(tick)}
            </div>
          );
        })}
      </div>
      <div className="relative min-w-0 flex-1">
        {children}
        {scale.ticks.map((tick, index) => (
          <div
            key={`line-${tick}-${index}`}
            className="pointer-events-none absolute left-0 right-0 border-t border-borderDefault"
            style={{ top: `${tickRatio(scale, tick) * 100}%` }}
          />
        ))}
      </div>
    </div>
  );
}

function CostLine({ values, scale }: { values: number[]; scale: ChartScale }) {
  const span = scale.max - scale.min || 1;
  const coords = values.map((value, index) => {
    const x = values.length <= 1 ? 50 : (index / (values.length - 1)) * 100;
    const y = scale.max <= scale.min ? 40 : ((scale.max - value) / span) * 40;
    return { x, y };
  });
  const line = coords.map((point) => `${point.x},${point.y}`).join(' ');
  const area = `0,40 ${line} 100,40`;
  const last = coords[coords.length - 1];
  return (
    <svg viewBox="0 0 100 40" className="absolute inset-0 h-full w-full text-teal-400" preserveAspectRatio="none" aria-hidden="true">
      <polygon points={area} className="fill-teal-400/20" />
      <polyline points={line} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      {last && <circle cx={last.x} cy={last.y} r="1.8" className="fill-teal-200" />}
    </svg>
  );
}

const ProductionCostWidget: React.FC = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const locale = language === 'en' ? 'en-GB' : language === 'lt' ? 'lt-LT' : language === 'de' ? 'de-DE' : 'sv-SE';
  const organizationId = useAuthStore((state) => state.user?.organization_id);
  const [span, setSpan] = useState<ProductionSpan>('months');
  const range = rollingYearRange(new Date());

  const summary = useQuery(
    ['production-cost', organizationId, range.startDate, range.endDate],
    async () => {
      const response = await api.get<SummaryResponse>('/api/reports/executive/summary', {
        params: { organizationId, startDate: range.startDate, endDate: range.endDate },
      });
      if (!response.data.success) throw new Error('Failed to load production cost');
      return response.data.data;
    },
    { enabled: organizationId != null && organizationId !== '', staleTime: 60 * 1000 }
  );

  const rows: ProductionPoint[] = (summary.data?.trend ?? []).map((point) => ({
    period: point.period,
    distanceKm: point.distanceKm,
    costKr: point.fuelCost,
  }));
  const series = seriesForSpan(span, new Date(), rows);
  const latest = series[series.length - 1];
  const previous = series[series.length - 2];
  const kmChange = latest && previous && latest.distanceKm > 0 ? percentChange(latest.distanceKm, previous.distanceKm) : null;
  const totalKm = series.reduce((sum, point) => sum + point.distanceKm, 0);
  const kmScale = chartScale(series.map((point) => point.distanceKm), true);
  const costScale = chartScale(series.map((point) => point.costKr), false);
  const vehicles = summary.data?.kpis?.totalVehicles ?? 0;
  const months = Math.max(series.length, 1);
  const kmPerVehicle = vehicles > 0 ? totalKm / vehicles / months : 0;
  const periodTco = summary.data?.kpis?.totalTco ?? 0;
  const avgKrPerKm = summary.data?.kpis?.avgCostPerKm ?? 0;
  const failed = summary.isError;
  const waiting = summary.isLoading && !summary.data;
  const dash = '–';

  const spanLabel = (value: ProductionSpan) => t(`productionCost.span.${value}`);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between gap-3">
        <h2 className="truncate text-base font-semibold text-textDefault">{t('productionCost.title')}</h2>
        <div className="flex shrink-0 rounded-lg bg-bgElevated p-0.5 text-xs">
          {SPANS.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setSpan(value)}
              className={`rounded-md px-2 py-1 ${span === value ? 'bg-bgCard text-textDefault' : 'text-textSecondary'}`}
            >
              {spanLabel(value)}
            </button>
          ))}
        </div>
      </div>

      {failed && <div className="mt-2 text-sm text-textTertiary">{t('productionCost.noData')}</div>}

      <div className="mt-3 grid min-h-0 flex-1 grid-cols-2 grid-rows-[auto_minmax(18rem,1fr)_auto] gap-x-6 gap-y-2">
        <div className="min-w-0">
          <div className="text-xs text-textSecondary">{t('productionCost.kmTitle')}</div>
          <div className="mt-1 flex items-end justify-between gap-2">
            <div className="text-2xl font-semibold tabular-nums text-textDefault">
              {failed || waiting || !latest ? dash : formatCompactAmount(latest.distanceKm, 'km')}
            </div>
            {kmChange != null && previous && (
              <div className={`text-xs ${kmChange >= 0 ? 'text-teal-400' : 'text-rose-400'}`}>
                {t('productionCost.vsPrevious')
                  .replace('{percent}', `${kmChange > 0 ? '+' : ''}${kmChange} %`)
                  .replace('{month}', monthLabel(previous.period, locale))}
              </div>
            )}
          </div>
          <div className="text-xs text-textTertiary">
            {latest ? t('productionCost.inMonth').replace('{month}', monthLabel(latest.period, locale)) : ''}
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="text-xs text-textSecondary">{t('productionCost.fuelTitle')}</div>
            <div className="text-xs text-textSecondary">
              {failed || waiting || !latest || latest.distanceKm <= 0 ? '' : formatKrPerKm(latest.costKr / latest.distanceKm)}
            </div>
          </div>
          <div className="mt-1 text-2xl font-semibold tabular-nums text-textDefault">
            {failed || waiting || !latest ? dash : formatCompactAmount(latest.costKr, 'kr')}
          </div>
          <div className="text-xs text-textTertiary">
            {latest ? t('productionCost.inMonth').replace('{month}', monthLabel(latest.period, locale)) : ''}
          </div>
        </div>

        <AxisPlot scale={kmScale}>
          <div className="absolute inset-0 flex items-end gap-1">
            {series.map((point, index) => (
              <div key={point.period} className="flex h-full min-w-0 flex-1 items-end">
                <div
                  className={`w-full rounded-t ${index === series.length - 1 ? 'bg-teal-300' : 'bg-teal-500/80'}`}
                  style={{ height: `${point.distanceKm > 0 && kmScale.max > 0 ? Math.max(4, (point.distanceKm / kmScale.max) * 100) : 0}%` }}
                />
              </div>
            ))}
          </div>
        </AxisPlot>
        <AxisPlot scale={costScale}>
          <CostLine values={series.map((point) => point.costKr)} scale={costScale} />
        </AxisPlot>

        <div className="flex gap-1 pl-11">
          {series.map((point) => (
            <div key={point.period} className="min-w-0 flex-1 truncate text-center text-[10px] text-textTertiary">
              {monthLabel(point.period, locale)}
            </div>
          ))}
        </div>
        <div />
      </div>

      <div className="mt-3 grid shrink-0 grid-cols-4 gap-3 border-t border-borderDefault pt-3 text-sm">
        <div>
          <div className="text-xs text-textTertiary">{t('productionCost.totalKm')}</div>
          <div className="font-semibold tabular-nums text-textDefault">{failed || waiting ? dash : formatCompactAmount(totalKm, 'km')}</div>
        </div>
        <div>
          <div className="text-xs text-textTertiary">{t('productionCost.periodCost')}</div>
          <div className="font-semibold tabular-nums text-textDefault">{failed || waiting ? dash : formatCompactAmount(periodTco, 'kr')}</div>
        </div>
        <div>
          <div className="text-xs text-textTertiary">{t('productionCost.perKm')}</div>
          <div className="font-semibold tabular-nums text-textDefault">{failed || waiting ? dash : formatKrPerKm(avgKrPerKm)}</div>
        </div>
        <div className="flex items-end justify-between gap-2">
          <div>
            <div className="text-xs text-textTertiary">{t('productionCost.perVehicle')}</div>
            <div className="font-semibold tabular-nums text-textDefault">{failed || waiting || vehicles === 0 ? dash : formatCompactAmount(kmPerVehicle, 'km')}</div>
          </div>
          <Link to={ROUTES.REPORTS_FLEET_TCO} className="shrink-0 text-xs text-teal-400 hover:underline">
            {t('productionCost.link')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProductionCostWidget;
